import { type ReactNode, createContext, forwardRef, useContext, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { tokens } from '../../tokens';
import { Pressable, type PressableProps } from '../pressable/pressable';

import { ButtonActivityIndicator } from './button-activity-indicator';
import type { ButtonSize, ButtonType } from './types';

export type ButtonContextType = {
  type: ButtonType;
  size: ButtonSize;
  disabled: boolean;
  loading: boolean;
};

const ButtonContext = createContext<ButtonContextType>({
  type: 'solid',
  size: 'md',
  disabled: false,
  loading: false,
});

export function useButtonContext() {
  return useContext(ButtonContext);
}

export type ButtonProps = Omit<PressableProps, 'children'> & {
  children: ReactNode;
  type?: ButtonType;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  sharedTransitionTag?: string;
};

export const ButtonFrame = forwardRef<View, ButtonProps>(function ButtonFrame(
  {
    type = 'solid',
    size = 'md',
    disabled = false,
    loading = false,
    children,
    ...pressableProps
  }: ButtonProps,
  forwardedRef
) {
  return (
    <ButtonContext.Provider
      value={useMemo(
        () => ({
          type,
          size,
          disabled,
          loading,
        }),
        [type, size, disabled, loading]
      )}
    >
      <Pressable
        ref={forwardedRef}
        {...pressableProps}
        disabled={disabled || loading}
        style={[
          buttonStyles.baseline,
          buttonStyles[type],
          buttonStyles[size],
          pressableProps.style,
        ]}
        borderRadius={pressableProps.borderRadius ?? 800}
        hoveredStyle={[buttonHoveredStyles[type], pressableProps.hoveredStyle]}
        pressedStyle={[buttonPressedOrFocusedStyles[type], pressableProps.pressedStyle]}
        focusedStyle={[buttonPressedOrFocusedStyles[type], pressableProps.focusedStyle]}
        disabledStyle={[
          disabled ? buttonDisabledStyles[type] : undefined,
          pressableProps.disabledStyle,
        ]}
      >
        {loading && <ButtonActivityIndicator size={size} type={type} disabled={disabled} />}
        {children}
      </Pressable>
    </ButtonContext.Provider>
  );
});

const buttonStyles = StyleSheet.create({
  baseline: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  solid: {
    backgroundColor: tokens.colors.$houseRed,
    borderColor: tokens.colors.$houseRed,
  },
  'solid-inverted': {
    backgroundColor: tokens.colors.$white,
    borderColor: tokens.colors.$transparent,
  },
  outline: {
    backgroundColor: tokens.colors.$transparent,
    borderColor: tokens.colors.$houseRed,
  },
  'outline-inverted': {
    backgroundColor: tokens.colors.$transparent,
    borderColor: tokens.colors.$white,
  },
  'outline-subtle': {
    backgroundColor: tokens.colors.$transparent,
    borderColor: tokens.colors.$blackOpacity10,
  },
  ghost: {
    backgroundColor: tokens.colors.$transparent,
    borderColor: tokens.colors.$transparent,
  },
  xs: {
    paddingHorizontal: 16,
    minHeight: 31,
  },
  sm: {
    paddingHorizontal: 16,
    minHeight: 34,
  },
  md: {
    paddingHorizontal: 16,
    minHeight: 37,
  },
  lg: {
    paddingHorizontal: 16,
    minHeight: 40,
  },
  xl: {
    paddingHorizontal: 28,
    minHeight: 48,
  },
});

const buttonHoveredStyles = StyleSheet.create({
  solid: {
    backgroundColor: tokens.colors.$houseRedDarken,
    borderColor: tokens.colors.$houseRedDarken,
  },
  'solid-inverted': {
    backgroundColor: tokens.colors.$whiteOpacity90,
  },
  outline: {
    backgroundColor: tokens.colors.$houseRed10,
  },
  'outline-subtle': {
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  'outline-inverted': {
    backgroundColor: tokens.colors.$whiteOpacity10,
  },
  ghost: {
    backgroundColor: tokens.colors.$houseRed10,
  },
});

const buttonPressedOrFocusedStyles = StyleSheet.create({
  solid: {
    backgroundColor: tokens.colors.$houseRedDarken,
    borderColor: tokens.colors.$houseRedDarken,
  },
  'solid-inverted': {
    backgroundColor: tokens.colors.$whiteOpacity90,
  },
  outline: {
    backgroundColor: tokens.colors.$transparent,
  },
  'outline-subtle': {
    backgroundColor: tokens.colors.$transparent,
  },
  'outline-inverted': {
    backgroundColor: tokens.colors.$whiteOpacity10,
  },
  ghost: {
    backgroundColor: tokens.colors.$transparent,
  },
});

const buttonDisabledStyles = StyleSheet.create({
  solid: {
    backgroundColor: tokens.colors.$blackOpacity04,
    borderColor: tokens.colors.$black10,
  },
  'solid-inverted': {
    backgroundColor: tokens.colors.$whiteOpacity30,
    borderColor: tokens.colors.$transparent,
  },
  outline: {
    borderColor: tokens.colors.$blackOpacity10,
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  'outline-inverted': {
    borderColor: tokens.colors.$whiteOpacity30,
    backgroundColor: tokens.colors.$transparent,
  },
  'outline-subtle': {
    borderColor: tokens.colors.$blackOpacity10,
    backgroundColor: tokens.colors.$blackOpacity10,
  },
  ghost: {},
});
